import { z } from 'zod';
/**
 * Schemas Zod compartilhados entre web e api para fluxo de autenticação.
 *
 * Princípios:
 *   - Senha mínima 12 chars + 3 das 4 classes (minúscula, maiúscula, número, especial)
 *   - E-mail em lowercase / trim aplicado no schema
 *   - Tokens (verify, reset) sempre random base64url 32 bytes — schema só valida formato
 *
 * Veja docs/10-auth.md.
 */
const passwordRules = (val) => {
    if (val.length < 12)
        return false;
    let classes = 0;
    if (/[a-z]/.test(val))
        classes += 1;
    if (/[A-Z]/.test(val))
        classes += 1;
    if (/\d/.test(val))
        classes += 1;
    if (/[^A-Za-z0-9]/.test(val))
        classes += 1;
    return classes >= 3;
};
export const PasswordSchema = z
    .string()
    .min(12, 'Mínimo 12 caracteres')
    .max(200, 'Máximo 200 caracteres')
    .refine(passwordRules, {
    message: 'Use ao menos 3 destes: minúscula, maiúscula, número, caractere especial',
});
export const EmailSchema = z
    .string()
    .trim()
    .toLowerCase()
    .email('E-mail inválido')
    .max(254, 'E-mail muito longo');
export const DisplayNameSchema = z
    .string()
    .trim()
    .min(2, 'Nome muito curto')
    .max(80, 'Nome muito longo');
export const RegisterRequestSchema = z.object({
    email: EmailSchema,
    password: PasswordSchema,
    displayName: DisplayNameSchema,
});
export const LoginRequestSchema = z.object({
    email: EmailSchema,
    password: z.string().min(1, 'Senha obrigatória'),
});
export const PasswordResetRequestSchema = z.object({
    email: EmailSchema,
});
export const PasswordResetConfirmSchema = z.object({
    token: z.string().min(20).max(200),
    newPassword: PasswordSchema,
});
export const VerifyEmailRequestSchema = z.object({
    token: z.string().min(20).max(200),
});
/**
 * Resposta pública de user (sem senha hash, sem tokens).
 */
export const PublicUserSchema = z.object({
    id: z.string().uuid(),
    email: z.string(),
    displayName: z.string(),
    emailVerified: z.boolean(),
    isAdmin: z.boolean().default(false),
    createdAt: z.string(),
});
export const AuthResponseSchema = z.object({
    user: PublicUserSchema,
});
// ============================================================
// 2FA TOTP
// ============================================================
export const TotpCodeSchema = z
    .string()
    .trim()
    .regex(/^\d{6}$/, 'Código deve ter 6 dígitos');
export const TotpRecoveryCodeSchema = z
    .string()
    .trim()
    .regex(/^[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}$/i, 'Formato xxxx-xxxx-xxxx');
export const TotpSetupResponseSchema = z.object({
    secretBase32: z.string(),
    otpauthUrl: z.string(),
    recoveryCodes: z.array(z.string()).length(10),
});
export const TotpEnableRequestSchema = z.object({
    secretBase32: z.string().min(16),
    recoveryCodes: z.array(z.string()).length(10),
    code: TotpCodeSchema,
});
export const TotpDisableRequestSchema = z.object({
    password: z.string().min(1),
});
export const TotpVerifyRequestSchema = z.object({
    challengeToken: z.string().min(20),
    code: z.union([TotpCodeSchema, TotpRecoveryCodeSchema]),
});
/**
 * Resposta do /auth/login estendida — pode ser auth completa ou pedir 2FA.
 */
export const LoginResponseSchema = z.union([
    z.object({
        requires2fa: z.literal(false),
        user: PublicUserSchema,
    }),
    z.object({
        requires2fa: z.literal(true),
        challengeToken: z.string(),
        expiresAt: z.string(),
    }),
]);
export const TotpStatusSchema = z.object({
    enabled: z.boolean(),
    enabledAt: z.string().nullable(),
    recoveryCodesRemaining: z.number().int(),
    lastUsedAt: z.string().nullable(),
});
/**
 * Calcula score simplificado de senha (0-4) — usado no frontend para feedback
 * visual. Não é decisão de segurança (validação real está em PasswordSchema).
 */
export function passwordScore(value) {
    if (value.length === 0)
        return { score: 0, label: '—' };
    if (value.length < 8)
        return { score: 1, label: 'fraca' };
    let classes = 0;
    if (/[a-z]/.test(value))
        classes += 1;
    if (/[A-Z]/.test(value))
        classes += 1;
    if (/\d/.test(value))
        classes += 1;
    if (/[^A-Za-z0-9]/.test(value))
        classes += 1;
    if (value.length < 12)
        return { score: 2, label: 'média' };
    if (classes >= 3 && value.length >= 14)
        return { score: 4, label: 'excelente' };
    if (classes >= 3)
        return { score: 3, label: 'boa' };
    return { score: 2, label: 'média' };
}
//# sourceMappingURL=auth.js.map